<template>
  <div>
    <div
      class="upload-files"
      @click="$refs.fileInput.click()"
      @dragenter.prevent
      @dragover.prevent
      @drop.prevent.stop="handleFileDrop"
    >
      <div>
        <v-icon>mdi-file-document-outline</v-icon>
      </div>
      <h3>{{ $t('instructions.uploadFiles.dragHere') }}</h3>
      <h5>{{ $t('instructions.uploadFiles.maxFiles') }}</h5>

      <div class="d-flex justify-center my-5 align-center">
        <v-btn outlined color="primary" class="mx-2" @click.stop="$refs.fileInput.click()">
         <span class="white--text"> {{ $t('instructions.uploadFiles.filesSelection') }}  </span>
        </v-btn>
    
        <input
          type="file"
          multiple
          @change="handleFilesUpload"
          ref="fileInput"
          data-testid="file-drop-input"
          :accept="accept"
          style="display: none"
        />
      </div>
    </div>

    <div v-if="value.length" class="d-flex flex-column my-3">
      <div
        v-for="(file, index) in value"
        :key="file.name + index"
        class="d-flex align-center justify-space-between staged-file"
      >
        <span>{{ fileName(file.name) }} - {{ (file.size / 1024 / 1024).toFixed(3) }} MB</span>
        <v-btn icon x-small @click="removeFile(index)"><v-icon x-small>mdi-close</v-icon></v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import { useSettingsStore } from '@/store'
import { bytesToDisplayMB } from '@/utils'

// Pure so it can also be used to seed a staged-files list from outside
// (e.g. files picked before this component is mounted).
export function selectPdfFiles ({ existingFiles = [], incoming, maxFiles = 4, maxSize }) {
  const incomingPdfs = Array.from(incoming || []).filter(file =>
    file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
  )
  const fileKey = file => `${file.name}:${file.size}:${file.lastModified}`
  const existingKeys = new Set(existingFiles.map(fileKey))
  const uniqueNewFiles = incomingPdfs.filter(file => {
    const key = fileKey(file)
    if (existingKeys.has(key)) return false
    existingKeys.add(key)
    return true
  })

  const room = Math.max(maxFiles - existingFiles.length, 0)
  const withinRoom = uniqueNewFiles.slice(0, room)
  const extraFiles = uniqueNewFiles.slice(room).map(f => f.name)
  const accepted = withinRoom.filter(f => f.size <= maxSize)
  const rejectedOversize = withinRoom.filter(f => f.size > maxSize).map(f => f.name)

  return {
    files: [...existingFiles, ...accepted],
    extraFiles,
    rejectedOversize
  }
}

export default {
  name: 'FileDropUpload',
  props: {
    value: { type: Array, default: () => [] },
    maxFiles: { type: Number, default: 4 },
    maxSize: { type: Number, required: true },
    accept: { type: String, default: '.pdf' }
  },
  methods: {
    fileName (name) {
      return useSettingsStore().formatFileName(name)
    },
    handleFileDrop (event) {
      this.addFiles(event.dataTransfer.files)
    },
    handleFilesUpload (event) {
      this.addFiles(event.target.files)
      if (this.$refs.fileInput) this.$refs.fileInput.value = null
    },
    addFiles (fileList) {
      const { files, extraFiles, rejectedOversize } = selectPdfFiles({
        existingFiles: this.value,
        incoming: fileList,
        maxFiles: this.maxFiles,
        maxSize: this.maxSize
      })

      this.$emit('input', files)

      if (extraFiles.length) {
        this.$emit('error', `Too many files selected! File(s) ${extraFiles.join(', ')} was not uploaded`)
      }
      if (rejectedOversize.length) {
        this.$emit('error', `File(s) too large (max ${bytesToDisplayMB(this.maxSize)}): ${rejectedOversize.join(', ')}`)
      }
    },
    removeFile (index) {
      const files = this.value.slice()
      const [removed] = files.splice(index, 1)
      this.$emit('input', files)
      if (removed) this.$emit('remove', removed)
    }
  }
}
</script>

<style scoped>
.upload-files {
  cursor: pointer;
  border: 2px dashed rgba(255, 255, 255, 0.5);
  border-radius: 10px;
  width: 100%;
  padding-top: 2rem;
  padding-bottom: 2rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;
}
.staged-file {
  padding: .25rem 0;
}
</style>
